<script setup lang="ts" generic="T">
  import { type CellRenderProps, VTooltip } from "@krainovsd/vue-ui";
  import dayjs from "dayjs";
  import { computed } from "vue";
  import ChevronDown from "@/components/icons/ChevronDown.vue";
  import ChevronRight from "@/components/icons/ChevronRight.vue";
  import CornerDownRight from "@/components/icons/CornerDownRight.vue";
  import { deepGroupByRow$ } from "../table.events";

  type Props = {
    withDeepGroup?: boolean;
    // When `isDate` is set, a non-null parent cell value is rendered through
    // dayjs using the `formatDate` pattern (e.g. "DD.MM.YYYY").
    isDate?: boolean;
    formatDate?: string;
    // Field read from a sub-row (child) to display instead of the column's
    // grouping value — e.g. the "name" or "date" carried by splitting rows.
    childNameField?: string;
    // The child counterpart of `isDate`: when set, child values are
    // date-formatted with `childFormatDate` (falling back to `formatDate`).
    childIsDate?: boolean;
    // The child counterpart of `formatDate`, so a parent and its sub-grouping
    // can use different date formats (e.g. month "MM.YYYY" over days "DD.MM.YYYY").
    childFormatDate?: string;
    // Prefix the cell with the row's entity id when it's a positive number,
    // e.g. "123 - Trigger name". Date rows carry no id, so they show bare.
    withId?: boolean;
    // Wrap onto up to two lines (with an ellipsis past that) instead of a
    // single truncated line.
    multiline?: boolean;
  };

  const props = defineProps<CellRenderProps<T, Props>>();

  // A real entity id is a positive number; `0`/undefined (date or placeholder
  // rows) means "no id", so the value is shown without a prefix.
  function hasEntityId(value: unknown): value is number {
    return typeof value === "number" && value > 0;
  }

  // Child rows (depth > 0) show their own `childNameField` value when one is
  // configured; otherwise the column's accessor value is used.
  const isChild = computed(
    () => props.context.row.depth > 0 && Boolean(props.settings?.childNameField),
  );
  const content = computed(() => {
    const childNameField = props.settings?.childNameField;
    if (isChild.value && childNameField) {
      const childValue = (props.context.row.original as Record<string, unknown>)[childNameField];
      if (childValue !== null && childValue !== undefined) return childValue;
    }
    return props.context.column.accessorFn?.(props.context.row.original, props.context.row.index);
  });

  // Format the raw value as a date when configured, falling back to the raw
  // value if it can't be parsed. Parent rows use `isDate`, child rows
  // `childIsDate`, so each level follows its own grouping dimension.
  const displayValue = computed<string | number | undefined>(() => {
    const value = content.value;
    const settings = props.settings;
    const shouldFormatDate = isChild.value ? settings?.childIsDate : settings?.isDate;
    const formatDate = isChild.value
      ? (settings?.childFormatDate ?? settings?.formatDate)
      : settings?.formatDate;

    let text: string | number | undefined = value as string | number | undefined;
    if (shouldFormatDate && formatDate && value !== null && value !== undefined) {
      const parsed = dayjs(value as string | number | Date);
      if (parsed.isValid()) text = parsed.format(formatDate);
    }

    // Prefix with the row's entity id (e.g. "123 - Trigger name") when enabled
    // and present; date rows have no id and stay bare.
    const id = (props.context.row.original as { id?: unknown }).id;
    if (settings?.withId && hasEntityId(id) && text !== null && text !== undefined && text !== "") {
      return `${id} - ${text}`;
    }
    return text;
  });
  const isExpandable = computed(
    () => props.context.cell.row.getIsGrouped() || props.context.cell.row.getCanExpand(),
  );
  const isExpanded = computed(() => props.context.row.getIsExpanded());
</script>

<template>
  <div :class="[$style.base, $props.context.row.depth > 0 && $style.child]">
    <ChevronDown
      v-if="isExpandable && isExpanded"
      :class="$style.expander"
      @click.prevent.stop="() => props.context.row.toggleExpanded(!isExpanded)"
      @dblclick.prevent.stop=""
    />
    <ChevronRight
      v-else-if="isExpandable"
      :class="$style.expander"
      @click.prevent.stop="() => props.context.row.toggleExpanded(!isExpanded)"
      @dblclick.prevent.stop=""
    />
    <!-- Full value on hover, shown only when the cell is actually truncated. -->
    <VTooltip :class="$style.tooltip" :open-not-visible="true" :text="displayValue">
      <span
        class="ksd-table__cell-default-text"
        :class="[
          $style.text,
          !isExpandable && $style.empty,
          $props.settings?.multiline && $style.multiline,
        ]"
      >
        {{ displayValue }}
      </span>
    </VTooltip>
    <CornerDownRight
      v-if="$props.context.row.depth === 0"
      :class="$style.cornerDownRight"
      @click="
        deepGroupByRow$.next({
          row: props.context.cell.row as unknown as Record<string, unknown>,
          currentKey: content as string,
        })
      "
    />
  </div>
</template>

<style lang="scss" module>
  .base {
    display: flex;
    align-items: center;
    gap: 0.375rem;

    width: 100%;
    min-width: 0;
    overflow: hidden;
  }

  // Indent child (depth > 0) rows so their content sits under the parent.
  .child {
    padding-left: 40px !important;
  }

  // Truncate a single line with an ellipsis instead of wrapping. The
  // `ksd-table__cell-default-text` class already supplies overflow/ellipsis,
  // so we only add `white-space: nowrap` and let the cell flex to fill space.
  .text {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
  }

  // Wrap onto at most two lines, clipping with an ellipsis beyond that. The
  // doubled selector outranks the library's `.ksd-table__cell-default-text`
  // (which forces `display: block`) so the `-webkit-box` line clamp takes effect.
  .multiline.multiline {
    white-space: normal;

    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    line-clamp: 3;

    overflow: hidden;
  }

  .tooltip {
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }

  .cornerDownRight {
    $size: 0.875rem;
    flex-shrink: 0;
    color: var(--muted-foreground);
    width: $size;
    height: $size;
  }

  .expander {
    $size: 0.875rem;
    flex-shrink: 0;
    cursor: pointer;
    color: var(--muted-foreground);
    width: $size;
    height: $size;
  }
</style>
